<template>
  <div class="leave-schedule">
    <div v-if="isSalesLeave" class="ui-calendar-workspace">
    <app-filter-toolbar>
      <app-select
        v-model="departmentFilter"
        design="new"
        :options="departmentOptions"
        placeholder="Department"
        aria-label="Department"
        @input="fetchItems"
      />
      <app-select
        v-model="positionFilter"
        design="new"
        :options="positionOptions"
        placeholder="Position"
        aria-label="Position"
        @input="fetchItems"
      />
      <div class="ui-date-field date-picker-wrapper">
        <app-date-range-picker
          id="leave_schedule_dates"
          v-model="filterDates"
          :custom-shortcuts="customShortcuts"
          :shortcut="$route.query.from_date && $route.query.to_date ? null : 'next30Days'"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Dates"
          aria-label="Dates"
          overlay
          no-header
          no-label
          locale="en"
          range
          no-clear-button
          @is-hidden="onDatesHidden"
        />
        <div v-if="datePickerError" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <template #selection>
        <app-filters
          v-model="statusFilter"
          design="new"
          refined
          weight="medium"
          :filters="statusOptions"
          name="leaveScheduleStatus"
          aria-label="Status"
          @change="fetchItems"
        />
      </template>
      <template #actions>
        <details class="ui-legend">
          <summary v-tooltip="{ content: 'Legend', placement: 'left', offset: 8, classes: 'tooltip--new' }" class="ui-legend__button ui-legend__button--icon" aria-label="Legend">
            <i class="pi pi-list" aria-hidden="true" />
          </summary>
          <div class="ui-legend__panel">
            <strong>Leave type</strong>
            <ul>
              <li v-for="item in visibleLegendItems" :key="item.code">
                <i :style="legendStyle(item.color)"></i>
                {{ item.name }}
              </li>
            </ul>
            <div class="ui-legend__note">
              <i></i>
              Dashed border means requested
            </div>
          </div>
        </details>
      </template>
      <template v-if="hasFilters" #clear>
        <app-clear-filters-button design="new" icon-only @clear="clearFilters" />
      </template>
    </app-filter-toolbar>
    <app-content-skeleton v-if="loading" layout="block" label="Loading leave schedule" />
    <app-error-state v-else-if="loadError" icon-only :message="loadError" @retry="fetchItems" />
    <app-no-results v-else-if="schedule && !schedule.instances.length" design="new" title="No Results" text="" />
    <div
      v-else-if="schedule"
      class="ui-calendar-surface"
    >
      <app-schedule
        design="new"
        :schedule="schedule"
        text-tooltips
        :need_search="false"
        :drag-from-header-only="true"
        :stacked-period-labels="true"
        :grouped-period-months="true"
        :fill-available-height="true"
        sidebar-label="Employee / Department"
        @event-click="openEvent"
      />
    </div>

    </div>
    <template v-else>
    <app-card design="new" padding="compact" class="leave-schedule__filter-card">
      <div class="leave-schedule__filter-grid">
        <app-form-group design="new" label="Dates" label-for="leave_schedule_dates-input">
          <div class="date-picker-wrapper leave-schedule__dates">
            <VueCtkDateTimePicker
              id="leave_schedule_dates"
              v-model="filterDates"
              class="custom-date-time-picker custom-date-time-picker--range leave-schedule__date-control"
              :custom-shortcuts="customShortcuts"
              :shortcut="$route.query.from_date && $route.query.to_date ? null : 'next30Days'"
              @is-hidden="onDatesHidden"
              format="YYYY-MM-DD"
              formatted="ll"
              label="Select dates"
              :color="uiVersion === 'classic' ? '#77b017' : '#1769e0'"
              :button-color="uiVersion === 'classic' ? '#77b017' : '#1769e0'"
              overlay
              no-header
              no-label
              locale="en"
              range
              noClearButton
            />
            <div v-if="datePickerError" class="date-picker-wrapper__error">Choose an end date</div>
          </div>
        </app-form-group>
        <app-form-group design="new" label="Status">
          <app-filters
            v-model="statusFilter"
            design="new"
            :filters="statusOptions"
            name="leaveScheduleStatus"
            @change="fetchItems"
          />
        </app-form-group>
        <app-form-group design="new" label="Department">
          <app-select
            v-model="departmentFilter"
            design="new"
            :options="departmentOptions"
            placeholder="All departments"
            @input="fetchItems"
          />
        </app-form-group>
        <app-form-group design="new" label="Position">
          <app-select
            v-model="positionFilter"
            design="new"
            :options="positionOptions"
            placeholder="All positions"
            @input="fetchItems"
          />
        </app-form-group>
      </div>
      <div class="leave-schedule__toolbar">
        <details class="leave-schedule__legend">
          <summary class="leave-schedule__legend-button">
            <img src="@/assets/img/hm/legend.svg" alt="">
            <span>Legend</span>
            <i aria-hidden="true"></i>
          </summary>
          <div class="leave-schedule__legend-panel">
            <strong>Leave type</strong>
            <ul>
              <li v-for="item in visibleLegendItems" :key="item.code">
                <i :style="legendStyle(item.color)"></i>
                {{ item.name }}
              </li>
            </ul>
            <div class="leave-schedule__legend-note">
              <i></i>
              Dashed border means requested
            </div>
          </div>
        </details>
      </div>
    </app-card>
    <app-spinner v-if="loading && !schedule" design="new" />
    <div
      v-else-if="schedule"
      class="leave-schedule__calendar"
      :class="{ 'leave-schedule__calendar--loading': loading }"
      :aria-busy="loading ? 'true' : 'false'"
    >
      <app-schedule
        :schedule="schedule"
        :need_search="false"
        :drag-from-header-only="true"
        :stacked-period-labels="true"
        :grouped-period-months="true"
        :fill-available-height="true"
        sidebar-label="Employee / Department"
        @event-click="openEvent"
      />
    </div>

    </template>

    <modal
      name="leave-schedule-detail"
      :width="560"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :click-to-close="!actionLoading"
      class="leave-schedule-detail-modal modal-not-overflow"
      draggable=".app-modal-header__copy"
      @closed="onDetailClosed"
    >
      <section class="leave-schedule-detail" aria-labelledby="leave-schedule-detail-title">
        <app-modal-header design="new" :title="detailTitle" title-id="leave-schedule-detail-title" @close="closeDetail" />
        <div v-if="detailLoading" class="leave-schedule-detail__body leave-detail__loading"><app-spinner design="new" /></div>
        <div v-else-if="detailEvent" class="leave-schedule-detail__body leave-detail">
          <div class="leave-detail__heading">
            <div>
              <span>Employee</span>
              <router-link :to="{ name: 'employees-profile', params: { id: detailEvent.employee.id } }">
                {{ detailEvent.employee.full_name }}
              </router-link>
              <small>{{ employeePosition }}</small>
            </div>
            <app-status-badge design="new" compact :label="statusName" :tone="statusTone" />
          </div>

          <div class="leave-detail__dates">
            <div><span>From</span><strong>{{ formatDate(detailEvent.from_date) }}</strong></div>
            <span aria-hidden="true">→</span>
            <div><span>To</span><strong>{{ formatDate(detailEvent.to_date) }}</strong></div>
            <b>{{ detailEvent.days }} day{{ detailEvent.days === 1 ? '' : 's' }}</b>
          </div>

          <div v-if="detailEvent.kind === 'change_dates'" class="leave-detail__reason">
            <span>Current dates (kept until approved)</span>
            <p>{{ formatDate(detailEvent.old_from_date) }} → {{ formatDate(detailEvent.old_to_date) }}</p>
          </div>

          <sales-leave-annual v-if="annual" :annual="annual" />

          <div class="leave-detail__facts">
            <div><span>Leave type</span><strong>{{ detailEvent.leave_type.name }}</strong></div>
            <div v-if="detailEvent.requested_at">
              <span>Requested</span>
              <strong :title="formatDateTime(detailEvent.requested_at)">{{ relativeDate(detailEvent.requested_at) }}</strong>
            </div>
          </div>

          <div class="leave-detail__document" :class="`leave-detail__document--${proofPresentation.state}`">
            <div>
              <span>Supporting document</span>
              <strong>{{ proofPresentation.name }}</strong>
              <small v-if="proofPresentation.description">{{ proofPresentation.description }}</small>
            </div>
            <a
              v-if="detailEvent.proof && detailEvent.proof.url"
              :href="detailEvent.proof.url"
              target="_blank"
              rel="noopener noreferrer"
            >Open document</a>
          </div>

          <div v-if="detailEvent.reason" class="leave-detail__reason">
            <span>Comment</span>
            <p>{{ detailEvent.reason }}</p>
          </div>

          <app-form-group v-if="rejectMode" design="new" label="Rejection reason" required>
            <app-textarea
              v-model="rejectionReason"
              design="new"
              placeholder="Rejection reason"
              :error="rejectSubmitted && !rejectionReason.trim()"
            />
            <template #error><div v-if="rejectSubmitted && !rejectionReason.trim()">Required field</div></template>
          </app-form-group>

          <footer v-if="hasDetailActions" class="leave-detail__actions">
            <template v-if="!rejectMode">
              <app-button
                v-if="detail.permissions.reject"
                design="new"
                theme="error"
                type="button"
                :disabled="actionLoading"
                @click.native="rejectMode = true"
              >Reject</app-button>
              <app-button
                v-if="detail.permissions.approve"
                ref="approveButton"
                design="new"
                theme="success"
                type="button"
                :disabled="actionLoading"
                @click.native="approveRequest"
              >Approve</app-button>
            </template>
            <template v-else>
              <app-button design="new" theme="dark" type="button" :disabled="actionLoading" @click.native="rejectMode = false">Back</app-button>
              <app-button
                ref="rejectButton"
                design="new"
                theme="error"
                type="button"
                :disabled="actionLoading"
                @click.native="rejectRequest"
              >Confirm rejection</app-button>
            </template>
          </footer>
        </div>
      </section>
    </modal>
  </div>
</template>

<script>
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import moment from 'moment/moment'
import AppSchedule from '@/components/AppSchedule.vue'
import SalesLeaveAnnual from '@/views/employees/sales-leave/SalesLeaveAnnual'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import {
  getEmployeesDepartmentList,
  getEmployeesLeaveScheduleEventDetail,
  getEmployeesLeavesCalendarV2,
  getEmployeesPositionList,
  getEmployeesSalesLeaveScheduleEventDetail,
  getEmployeesSalesLeavesCalendarV2,
  postEmployeesLeaveRequestApprove,
  postEmployeesLeaveRequestReject,
  postEmployeesSalesLeaveRequestApprove,
  postEmployeesSalesLeaveRequestReject,
} from '@/http'
import { formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'
import {
  LEGEND_ITEMS,
  STATUS_OPTIONS,
  buildLeaveSchedule,
  colorToRgba,
  createDefaultDates,
  getProofPresentation,
} from './leaveSchedule'

function scheduleDatesFromQuery(query) {
  if (typeof query.from_date !== 'string' || typeof query.to_date !== 'string') return createDefaultDates()
  const start = moment(query.from_date, 'YYYY-MM-DD', true)
  const end = moment(query.to_date, 'YYYY-MM-DD', true)
  if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') }
  }
  return createDefaultDates()
}

export default {
  name: 'EmployeesLeavesSchedule',
  components: {
    AppContentSkeleton,
    AppErrorState,
    AppSchedule,
    SalesLeaveAnnual,
    VueCtkDateTimePicker,
  },
  props: {
    uiVersion: { type: String, default: 'new' }
  },
  data() {
    return {
      loading: false,
      loadError: '',
      filterDates: scheduleDatesFromQuery(this.$route.query),
      datePickerError: false,
      customShortcuts: [
        { key: 'next30Days', label: 'Next 30 days', value: () => ({ start: moment(), end: moment().add(30, 'days') }) },
        { key: 'next60Days', label: 'Next 60 days', value: () => ({ start: moment(), end: moment().add(60, 'days') }) },
        { key: 'next90Days', label: 'Next 90 days', value: () => ({ start: moment(), end: moment().add(90, 'days') }) },
      ],
      statusFilter: STATUS_OPTIONS.some(option => option.value === this.$route.query.status) ? this.$route.query.status : STATUS_OPTIONS[0].value,
      statusOptions: STATUS_OPTIONS,
      departmentFilter: null,
      departmentOptions: [],
      positionFilter: null,
      positionOptions: [],
      legendItems: LEGEND_ITEMS,
      schedule: null,
      detail: null,
      detailSourceType: null,
      detailLoading: false,
      actionLoading: false,
      rejectMode: false,
      rejectSubmitted: false,
      rejectionReason: '',
      fetchRequestId: 0,
      detailRequestId: 0,
      isActive: true,
    }
  },
  computed: {
    hasFilters() {
      const dates = this.filterDates || {}
      const defaults = createDefaultDates()
      return Boolean(this.departmentFilter || this.positionFilter || this.statusFilter !== STATUS_OPTIONS[0].value || dates.start !== defaults.start || dates.end !== defaults.end)
    },
    isSalesLeave() {
      return this.$route.meta.leaveScope === 'sales'
    },
    visibleLegendItems() {
      if (!this.isSalesLeave) return this.legendItems
      return this.legendItems.filter(item => item.code !== 'parental_leave')
    },
    calendarParams() {
      return {
        from_date: this.filterDates.start,
        to_date: this.filterDates.end,
        status: this.statusFilter,
        department: this.departmentFilter ? this.departmentFilter.code : 'all',
        position: this.positionFilter ? this.positionFilter.code : 'all',
      }
    },
    detailEvent() {
      return this.detail && this.detail.event
    },
    detailTitle() {
      return this.detailSourceType === 'request' ? 'Leave request' : 'Approved leave'
    },
    hasDetailActions() {
      const permissions = this.detail && this.detail.permissions
      return Boolean(permissions && (permissions.approve || permissions.reject))
    },
    employeePosition() {
      if (!this.detailEvent) return ''
      return [this.detailEvent.position && this.detailEvent.position.name, this.detailEvent.department && this.detailEvent.department.name]
        .filter(Boolean)
        .join(' · ')
    },
    statusName() {
      if (!this.detailEvent) return ''
      if (this.detailEvent.status === 'requested') {
        return this.detailEvent.kind === 'change_dates' ? 'Date change requested' : 'Requested'
      }
      return this.detailEvent.change_pending ? 'Approved · Change pending' : 'Approved'
    },
    annual() {
      const impact = this.detail && this.detail.impact
      return impact && impact.kind === 'paid_annual' ? impact.annual : null
    },
    statusTone() {
      if (!this.detailEvent) return 'neutral'
      if (this.detailEvent.change_pending) return 'amber'
      return this.detailEvent.status === 'requested' ? 'blue' : 'green'
    },
    proofPresentation() {
      return getProofPresentation(this.detailEvent)
    },
  },
  created() {
    this.fetchFilterOptions()
    this.fetchItems()
  },
  beforeDestroy() {
    this.isActive = false
    this.fetchRequestId += 1
    this.detailRequestId += 1
  },
  methods: {
    clearFilters() {
      this.filterDates = createDefaultDates()
      this.statusFilter = STATUS_OPTIONS[0].value
      this.departmentFilter = null
      this.positionFilter = null
      this.datePickerError = false
      this.fetchItems()
    },
    fetchFilterOptions() {
      getEmployeesDepartmentList()
        .then(response => {
          if (!this.isActive) return
          this.departmentOptions = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {})
      getEmployeesPositionList()
        .then(response => {
          if (!this.isActive) return
          this.positionOptions = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {})
    },
    fetchItems() {
      if (!this.isActive || !this.filterDates || !this.filterDates.start || !this.filterDates.end) {
        return Promise.resolve()
      }
      const requestId = ++this.fetchRequestId
      this.loading = true
      this.loadError = ''
      const request = this.isSalesLeave ? getEmployeesSalesLeavesCalendarV2 : getEmployeesLeavesCalendarV2
      return request(this.calendarParams)
        .then(response => {
          if (requestId !== this.fetchRequestId) return
          const events = response.data && Array.isArray(response.data.events) ? response.data.events : []
          this.schedule = buildLeaveSchedule(events, this.filterDates)
        })
        .catch(() => {
          if (requestId === this.fetchRequestId && this.isSalesLeave) this.loadError = 'Unable to load leave schedule'
          if (requestId === this.fetchRequestId && !this.schedule && !this.isSalesLeave) {
            this.schedule = buildLeaveSchedule([], this.filterDates)
          }
        })
        .finally(() => {
          if (requestId === this.fetchRequestId) this.loading = false
        })
    },
    onDatesHidden() {
      if (this.filterDates && this.filterDates.start && this.filterDates.end) {
        this.datePickerError = false
        this.fetchItems()
      } else this.datePickerError = true
    },
    openEvent(event) {
      const requestId = ++this.detailRequestId
      this.detail = null
      this.detailSourceType = event.source_type
      this.detailLoading = true
      this.resetDecisionState()
      this.$modal.show('leave-schedule-detail')
      const request = this.isSalesLeave ? getEmployeesSalesLeaveScheduleEventDetail : getEmployeesLeaveScheduleEventDetail
      request(event.source_type, event.source_id)
        .then(response => {
          if (requestId === this.detailRequestId) this.detail = response.data
        })
        .catch(() => {
          if (requestId === this.detailRequestId) this.$modal.hide('leave-schedule-detail')
        })
        .finally(() => {
          if (requestId === this.detailRequestId) this.detailLoading = false
        })
    },
    closeDetail() {
      if (!this.actionLoading) this.$modal.hide('leave-schedule-detail')
    },
    onDetailClosed() {
      this.detailRequestId += 1
      this.detail = null
      this.detailSourceType = null
      this.detailLoading = false
      this.resetDecisionState()
    },
    resetDecisionState() {
      this.rejectMode = false
      this.rejectSubmitted = false
      this.rejectionReason = ''
    },
    approveRequest() {
      const event = this.detailEvent
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestApprove : postEmployeesLeaveRequestApprove
      this.runAction(
        () => request(event.source_id),
        'approveButton',
        'Leave request approved',
        event.id
      )
    },
    rejectRequest() {
      this.rejectSubmitted = true
      if (!this.rejectionReason.trim()) return
      const event = this.detailEvent
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestReject : postEmployeesLeaveRequestReject
      this.runAction(
        () => request(event.source_id, {
          decision_reason: this.rejectionReason.trim(),
        }),
        'rejectButton',
        'Leave request rejected',
        event.id
      )
    },
    runAction(requestFactory, ref, message, eventId) {
      if (this.actionLoading) return
      this.actionLoading = true
      this.setButtonPreload(ref, true)
      Promise.resolve()
        .then(requestFactory)
        .then(() => {
          if (!this.isActive) return
          this.removeScheduleEvent(eventId)
          return this.fetchItems()
        })
        .then(() => {
          if (!this.isActive) return
          this.$modal.hide('leave-schedule-detail')
          this.$notify({ type: 'success', title: message })
        })
        .catch(() => {})
        .finally(() => {
          if (!this.isActive) return
          this.actionLoading = false
          this.setButtonPreload(ref, false)
        })
    },
    removeScheduleEvent(eventId) {
      if (!this.schedule || !eventId) return
      const events = this.schedule.events.filter(event => event.id !== eventId)
      this.schedule = buildLeaveSchedule(events, this.filterDates)
    },
    setButtonPreload(ref, value) {
      if (this.$refs[ref]) this.$refs[ref].preload = value
    },
    formatDate(value) { return value ? moment(value).format('DD MMM YYYY') : '-' },
    formatDateTime(value) { return value ? moment(value).format('DD MMM YYYY, HH:mm') : '-' },
    relativeDate(value) { return formatRelativeDate(value) },
    legendStyle(color) {
      return { backgroundColor: colorToRgba(color, 0.4), borderColor: color }
    },
  },
}
</script>

<style lang="scss">
.leave-schedule-detail-modal > .vm--modal {
  border-radius: $ui-card-radius !important;
}

.leave-schedule-detail .app-modal-header--new {
  border-radius: $ui-card-radius $ui-card-radius 0 0;
}

.leave-schedule__filter-card .form-group--design-new {
  min-width: 0;
  margin-bottom: 0;
}

.leave-schedule__date-control.date-time-picker {
  width: 100%;
  min-width: 0;
  color: $ui-text;
  font-family: $ui-font-body;

  .field-input {
    height: $ui-control-height;
    min-height: $ui-control-height;
    border: 1px solid $ui-line-strong;
    border-radius: $ui-control-radius;
    background-color: $ui-surface;
    color: $ui-text;
    font-family: $ui-font-body;
    font-size: $ui-font-size-control;
    line-height: $ui-control-height;

    &:focus {
      border-color: var(--ui-accent, #{$ui-blue});
      outline: 0;
    }
  }
}

.leave-schedule__calendar .schedule {
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
  scrollbar-color: $ui-line-strong transparent;
}

.leave-schedule__calendar {
  .schedule__sidebar,
  .schedule-sidebar {
    width: 200px;
  }

  .schedule-sidebar__top,
  .schedule-calendar__time {
    border-color: $ui-line;
    background: $ui-surface-soft;
  }

  .schedule-sidebar__label,
  .schedule--stacked-period .schedule-calendar__time {
    color: $ui-muted;
    font-size: 11px;
    font-weight: 500;
  }

  .schedule--stacked-period .schedule-calendar__time { padding: 4px 1px; }

  .schedule-calendar__time small {
    color: $ui-subtle;
    font-size: $ui-font-size-metadata;
  }

  .schedule-sidebar__item,
  .schedule-calendar__col,
  .schedule-calendar__top {
    border-color: $ui-line;
  }

  .schedule--labeled-sidebar .schedule-sidebar__title {
    color: $ui-ink;
    font-size: $ui-font-size-body;
    font-weight: 600;
  }

  .schedule--labeled-sidebar .schedule-sidebar__description {
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 400;
  }

  .schedule-sidebar__item--selected,
  .schedule-calendar__row--selected .schedule-calendar__col,
  .schedule-calendar__col--selected,
  .schedule-calendar__time--selected {
    background: var(--ui-accent-soft, #{$ui-blue-soft});
  }

  .schedule-calendar__event {
    border-radius: $ui-control-radius;
    font-size: $ui-font-size-control;
    line-height: 1.25;
  }

  .schedule-calendar__title {
    font-weight: 600;
  }

  .schedule-calendar__description {
    font-size: $ui-font-size-metadata;
    font-weight: 400;
  }
}
</style>

<style lang="sass" scoped>
.leave-schedule
  position: relative
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0
  overflow: hidden
  color: $ui-text
  font-family: $ui-font-body
  font-size: $ui-font-size-body

.leave-schedule__filter-card
  position: relative
  z-index: 40
  flex: 0 0 auto
  margin-bottom: $ui-space-16

.leave-schedule__filter-grid
  display: grid
  grid-template-columns: minmax(240px, 1.25fr) auto minmax(150px, 1fr) minmax(150px, 1fr)
  align-items: end
  gap: $ui-space-12

.leave-schedule__calendar
  position: relative
  display: flex
  flex: 0 1 auto
  flex-direction: column
  width: 100%
  min-height: 0
  overflow: hidden

  &:before
    content: ''
    position: absolute
    top: 0
    left: 0
    z-index: 30
    width: 35%
    height: 3px
    background-color: $color-theme
    opacity: 0
    transform: translateX(-100%)
    pointer-events: none

.leave-schedule__calendar--loading:before
  opacity: 1
  animation: leave-schedule-loading 1.1s ease-in-out infinite

@keyframes leave-schedule-loading
  from
    transform: translateX(-100%)

  to
    transform: translateX(300%)

.leave-schedule__dates
  width: 100%
  min-width: 0

.leave-schedule__toolbar
  position: relative
  display: flex
  justify-content: flex-end
  margin-top: $ui-space-12

.leave-schedule__legend
  position: static

  summary
    list-style: none

    &::-webkit-details-marker
      display: none

  &[open] .leave-schedule__legend-button > i
    transform: translateY(2px) rotate(225deg)

.leave-schedule__legend-button
  min-height: 40px
  padding: 0 $ui-space-12
  border: 1px solid $ui-line-strong
  border-radius: $ui-control-radius
  box-sizing: border-box
  display: flex
  align-items: center
  gap: 9px
  background: $ui-surface
  color: $ui-text
  cursor: pointer
  font-family: $ui-font-body
  font-size: $ui-font-size-control
  font-weight: 500
  white-space: nowrap

  &:hover
    border-color: var(--ui-accent, #{$ui-blue})
    background: $ui-surface-soft

  &:focus-visible
    outline: 2px solid var(--ui-accent, #{$ui-blue})
    outline-offset: 2px

  img
    width: 18px
    height: 14px

  > i
    width: 8px
    height: 8px
    margin-left: 2px
    border-right: 2px solid $ui-muted
    border-bottom: 2px solid $ui-muted
    transform: translateY(-2px) rotate(45deg)
    transition: transform .15s ease

.leave-schedule__legend-panel
  position: absolute
  top: calc(100% + 8px)
  right: 0
  z-index: 50
  width: 300px
  max-width: 100%
  box-sizing: border-box
  padding: 18px
  border: 1px solid $ui-line
  border-radius: $ui-panel-radius
  background: $ui-surface
  box-shadow: $ui-card-shadow
  color: $ui-text
  font-family: $ui-font-body
  font-size: $ui-font-size-body

  > strong
    display: block
    margin-bottom: $ui-space-12
    color: $ui-ink
    font-size: $ui-font-size-body

  ul
    margin: 0
    padding: 0
    list-style: none

  li
    display: flex
    align-items: center
    gap: $ui-space-12
    margin-top: $ui-space-8
    color: $ui-text
    font-size: $ui-font-size-control

    > i
      width: 32px
      height: 22px
      border-left: 5px solid
      border-radius: 3px
      box-sizing: border-box

.leave-schedule__legend-note
  display: flex
  align-items: center
  gap: $ui-space-12
  margin-top: $ui-space-16
  padding-top: $ui-space-12
  border-top: 1px solid $ui-line
  color: $ui-muted
  font-size: $ui-font-size-metadata

  i
    width: 32px
    height: 22px
    border: 2px dashed $ui-muted
    border-radius: 3px
    background: $ui-surface-soft
    box-sizing: border-box

.leave-schedule-detail
  border-radius: $ui-card-radius
  background: $ui-surface
  color: $ui-text
  font-family: $ui-font-body

.leave-schedule-detail__body
  padding: $ui-space-24

.leave-detail
  display: grid
  grid-template-columns: minmax(0, 1fr)
  gap: $ui-space-16
  overflow-wrap: anywhere

  > *
    min-width: 0

.leave-detail__loading
  display: flex
  min-height: 180px
  align-items: center
  justify-content: center

.leave-detail__heading
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: $ui-space-16

  > div
    display: flex
    flex-direction: column
    gap: $ui-space-4
    min-width: 0

    span, small
      color: $ui-muted

    span
      font-size: $ui-font-size-metadata
      font-weight: 600
      text-transform: uppercase
      letter-spacing: .04em

  a
    color: var(--ui-link, var(--ui-accent, #{$ui-blue}))
    font-size: $ui-font-size-section-title
    font-weight: 600
    text-decoration: none

    &:hover
      text-decoration: underline

.leave-detail__dates
  display: grid
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto
  align-items: center
  gap: $ui-space-12
  padding: $ui-space-16
  border: 1px solid $ui-line
  border-radius: $ui-panel-radius
  background: $ui-surface-soft

  > div
    display: flex
    flex-direction: column
    gap: $ui-space-4

  span
    color: $ui-muted
    font-size: $ui-font-size-metadata
    text-transform: uppercase

  strong
    color: $ui-ink

  > b
    padding: $ui-space-8 $ui-space-12
    border-radius: $ui-control-radius
    background: $ui-surface
    color: $ui-text
    white-space: nowrap

.leave-detail__facts
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: $ui-space-12

  > div
    display: flex
    flex-direction: column
    gap: $ui-space-4
    padding: $ui-space-12 $ui-space-16
    border: 1px solid $ui-line
    border-radius: $ui-control-radius

  span
    color: $ui-muted
    font-size: $ui-font-size-metadata
    text-transform: uppercase

  strong
    color: $ui-ink

.leave-detail__document
  display: flex
  align-items: center
  justify-content: space-between
  gap: $ui-space-12
  padding: $ui-space-12 $ui-space-16
  border-left: 4px solid $ui-line-strong
  border-radius: $ui-control-radius
  background: $ui-surface-soft

  > div
    display: flex
    flex-direction: column
    gap: $ui-space-4
    min-width: 0

  span, small
    color: $ui-muted

  span
    font-size: $ui-font-size-metadata
    text-transform: uppercase

  a
    color: var(--ui-link, var(--ui-accent, #{$ui-blue}))
    font-weight: 600

.leave-detail__document--attached
  border-left-color: $ui-green
  background: $ui-green-soft

.leave-detail__document--missing
  border-left-color: $ui-amber
  background: $ui-amber-soft

.leave-detail__reason
  padding: $ui-space-12 $ui-space-16
  border: 1px solid $ui-line
  border-radius: $ui-control-radius
  background: $ui-surface-soft

  span
    color: $ui-muted
    font-size: $ui-font-size-metadata
    text-transform: uppercase

  p
    margin: $ui-space-4 0 0
    white-space: pre-wrap

.leave-detail__actions
  display: flex
  justify-content: flex-end
  flex-wrap: wrap
  gap: $ui-space-12
  padding-top: $ui-space-16
  border-top: 1px solid $ui-line

@media (max-width: 1100px)
  .leave-schedule__filter-grid
    grid-template-columns: repeat(2, minmax(0, 1fr))

@media (max-width: 620px)
  .leave-schedule__filter-grid
    grid-template-columns: minmax(0, 1fr)

  .leave-schedule-detail__body
    padding: $ui-space-16

  .leave-schedule__dates
    min-width: 0

  .leave-detail__heading
    flex-direction: column

  .leave-detail__dates
    grid-template-columns: 1fr 1fr

    > span
      display: none

  .leave-detail__facts
    grid-template-columns: 1fr
</style>
